'use client';

import { GoogleMap, OverlayView, OverlayViewF, PolylineF } from '@react-google-maps/api';
import React, { useEffect, useState } from 'react';

import { GoogleMapsLoader } from '@/components/google-maps-loader';
import { Icon } from '@/components/icon';
import {
  dashedLightPathOptions,
  dashedPathOptions,
  defaultMapOptions,
  outlinePathOptions,
  solidPathOptions,
} from '@/components/map/config';
import { MarkerDot } from '@/components/map-markers/marker-dot';
import { TrackingMarker } from '@/components/map-markers/tracking-marker';
import { MAP_LINE_STYLE_DASHED, MAP_LINE_STYLE_DASHED_LIGHT } from '@/lib/constants';
import { MapMarker, MapPath, MapPathStyleType } from '@/types';
import { tv } from '@/utils/styles';

interface MapProps {
  paths?: MapPath[];
  markers?: MapMarker[];
  shouldRefocusMap?: boolean;
  options?: google.maps.MapOptions;
  boundsPadding?: google.maps.Padding;
}

const getMapPathStyleOptions = (style: MapPathStyleType | undefined) => {
  switch (style) {
    case MAP_LINE_STYLE_DASHED:
      return dashedPathOptions;
    case MAP_LINE_STYLE_DASHED_LIGHT:
      return dashedLightPathOptions;
    default:
      return solidPathOptions;
  }
};

const BaseMiniMap = ({ paths, markers, shouldRefocusMap, options = {}, boundsPadding }: MapProps) => {
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  // Fits map to location markers when they change
  const fitMap = () => {
    if (mapInstance && markers?.length) {
      const bounds = new google.maps.LatLngBounds();
      markers.forEach((marker) => {
        if (marker && marker.location) {
          bounds.extend(marker.location);
        }
      });
      mapInstance.fitBounds(bounds, boundsPadding);
    }
  };

  useEffect(() => {
    fitMap();
  }, [markers, mapInstance]);

  useEffect(() => {
    if (shouldRefocusMap) {
      // use setTimeout to ensure any animation is finished before refocusing
      setTimeout(() => {
        fitMap();
      }, 200);
    }
  }, [shouldRefocusMap]);

  const onLoad = (loadedMap: google.maps.Map) => {
    setMapInstance(loadedMap);
  };

  const onUnmount = () => {
    setMapInstance(null);
  };

  const { base } = styles();

  return (
    <div className={base()}>
      <GoogleMap
        mapContainerClassName="gmap"
        center={defaultMapOptions.center}
        zoom={defaultMapOptions.zoom}
        options={{ ...defaultMapOptions.options, ...options }}
        onLoad={onLoad}
        onUnmount={onUnmount}
      >
        <>
          {markers?.length &&
            markers.map((marker, index) => {
              return marker.type === 'tracking' ? (
                <OverlayViewF
                  key={`${marker.id}-${index}`}
                  position={marker.location}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <TrackingMarker
                    color={marker.color}
                    type={marker.nodeType}
                    zIndex={marker.active ? markers.length + 20 : marker.zIndex}
                    location={marker.location}
                    previousLocation={markers[index - 1]?.location}
                    size={marker.size}
                    iconName={marker.icon || ''}
                    label={marker.label}
                    hasStaticLabel={marker.hasStaticLabel}
                    liveTracking={marker.liveTracking}
                    isActive={marker.active}
                    bearing={marker.bearing}
                  />
                </OverlayViewF>
              ) : (
                <OverlayViewF
                  key={`${marker.id}-${index}`}
                  position={marker.location}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <MarkerDot
                    backgroundColor={marker.color}
                    borderColor={marker.color}
                    size={marker.size}
                    isActive={marker.active}
                    showTooltip={marker.active}
                    zIndex={marker.active ? markers.length + index : marker.zIndex}
                    type={marker.nodeType}
                    tooltipContent={marker.label}
                    isOffset
                    className="border border-solid"
                  >
                    {marker.icon && <Icon name={marker.icon} color="white" />}
                    {!marker.icon && marker.value ? marker.value : ''}
                  </MarkerDot>
                </OverlayViewF>
              );
            })}
          {paths?.length &&
            paths.map((path, index) => {
              if (path.positions[0] && path.positions[1]) {
                return (
                  <React.Fragment key={`solid_${path.id}-${index}`}>
                    {/* outline */}
                    {path.style.hasOutline && (
                      <PolylineF
                        path={path.positions}
                        options={{
                          ...outlinePathOptions,
                          strokeColor: path.style.pathColor,
                          strokeOpacity: path.style.outlineOpacity,
                        }}
                      />
                    )}
                    {/* inner line */}
                    <PolylineF
                      path={path.positions}
                      options={{
                        ...getMapPathStyleOptions(path.style.pathStyle),
                        ...(path.style.zIndex ? { zIndex: path.style.zIndex } : {}),
                        ...(path.style.strokeWeight ? { strokeWeight: path.style.strokeWeight } : {}),
                        strokeColor: path.style.pathColor,
                      }}
                    />
                  </React.Fragment>
                );
              }
              return null;
            })}
        </>
      </GoogleMap>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'h-full w-full bg-grey-50 [&_.gmap]:h-full [&_.gmap]:w-full [&_.gmap_iframe+div]:!border-none',
  },
});

const MiniMap = (props: MapProps) => {
  return (
    <GoogleMapsLoader>
      <BaseMiniMap {...props} />
    </GoogleMapsLoader>
  );
};

export { MiniMap };
